/**
 * EBL / VRM / PI sheet — the three adjustable radar-style lines, all drawn from
 * own ship. The EBL is a bearing line to swing onto something; the VRM a range
 * circle to size to it; the PI (parallel index) a line at a set bearing offset a
 * set distance abeam, the radar coasting technique on the plotter: steer so the
 * headland rides along the line and the offset IS the passing distance
 * (fb-msmb1uso-6). Each has its own on/off and its own values, and each is drawn
 * in its own style so none reads as another.
 */
import { useChartPlotterStore } from '@/store';
import { useEblVrmStore } from '@/store/eblVrm';
import { formatBearing, formatDistance } from '@/nav';
import { haptic } from '@/ui/haptics';
import { Sheet } from './Sheet';

/** A labelled glass toggle row (matches the Layers/menu controls). */
function ToggleRow({
  title,
  detail,
  on,
  onClick,
}: {
  title: string;
  detail: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <div className="acp-layer">
      <div className="lt">
        <b>{title}</b>
        <span>{detail}</span>
      </div>
      <button
        type="button"
        className={`acp-sw${on ? ' on' : ''}`}
        aria-pressed={on}
        aria-label={title}
        onClick={() => {
          haptic('tick');
          onClick();
        }}
      />
    </div>
  );
}

export function EblVrmSheet() {
  const ownship = useChartPlotterStore((s) => s.ownship.position);

  const eblOn = useEblVrmStore((s) => s.eblOn);
  const setEblOn = useEblVrmStore((s) => s.setEblOn);
  const eblBearingDeg = useEblVrmStore((s) => s.eblBearingDeg);
  const setEblBearingDeg = useEblVrmStore((s) => s.setEblBearingDeg);
  const nudgeEbl = useEblVrmStore((s) => s.nudgeEbl);

  const vrmOn = useEblVrmStore((s) => s.vrmOn);
  const setVrmOn = useEblVrmStore((s) => s.setVrmOn);
  const vrmRangeNm = useEblVrmStore((s) => s.vrmRangeNm);
  const setVrmRangeNm = useEblVrmStore((s) => s.setVrmRangeNm);
  const nudgeVrm = useEblVrmStore((s) => s.nudgeVrm);

  const piOn = useEblVrmStore((s) => s.piOn);
  const setPiOn = useEblVrmStore((s) => s.setPiOn);
  const piBearingDeg = useEblVrmStore((s) => s.piBearingDeg);
  const setPiBearingDeg = useEblVrmStore((s) => s.setPiBearingDeg);
  const nudgePi = useEblVrmStore((s) => s.nudgePi);
  const piOffsetNm = useEblVrmStore((s) => s.piOffsetNm);
  const setPiOffsetNm = useEblVrmStore((s) => s.setPiOffsetNm);

  const ranges = [0.25, 0.5, 1, 2, 5, 10];

  return (
    <Sheet
      name="eblvrm"
      title="EBL / VRM"
      sub="A bearing line and a range circle from the boat, each set on its own. Drawn in their own style so they don't read like the range rings."
    >
      {!ownship && (
        <p className="acp-note">No fix yet, so there’s nowhere to draw them from. They’ll appear on the boat once we have a position.</p>
      )}

      {/* EBL — the bearing line */}
      <ToggleRow
        title="EBL — bearing line"
        detail={eblOn ? `Bearing ${formatBearing(eblBearingDeg)}` : 'Off'}
        on={eblOn}
        onClick={() => setEblOn(!eblOn)}
      />
      {eblOn && (
        <>
          <div className="acp-layer">
            <div className="lt">
              <b>Bearing</b>
              <span>{formatBearing(eblBearingDeg)}</span>
            </div>
            <input
              className="acp-range"
              style={{ flex: 1, minWidth: 0, maxWidth: 170 }}
              type="range"
              min={0}
              max={359}
              step={1}
              value={Math.round(eblBearingDeg)}
              aria-label="EBL bearing"
              onChange={(e) => setEblBearingDeg(Number(e.target.value))}
            />
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" aria-label="EBL minus 10 degrees" onClick={() => nudgeEbl(-10)}>−10°</button>
            <button type="button" className="acp-btn" aria-label="EBL minus 1 degree" onClick={() => nudgeEbl(-1)}>−1°</button>
            <button type="button" className="acp-btn" aria-label="EBL plus 1 degree" onClick={() => nudgeEbl(1)}>+1°</button>
            <button type="button" className="acp-btn" aria-label="EBL plus 10 degrees" onClick={() => nudgeEbl(10)}>+10°</button>
          </div>
        </>
      )}

      {/* VRM — the range circle */}
      <ToggleRow
        title="VRM — range circle"
        detail={vrmOn ? `Range ${formatDistance(vrmRangeNm)}` : 'Off'}
        on={vrmOn}
        onClick={() => setVrmOn(!vrmOn)}
      />
      {vrmOn && (
        <>
          <span className="acp-fieldlabel" style={{ marginTop: 8 }}>Range</span>
          <div className="picks">
            {ranges.map((v) => (
              <button
                key={v}
                type="button"
                className={Math.abs(vrmRangeNm - v) < 1e-6 ? 'on' : ''}
                onClick={() => {
                  haptic('tick');
                  setVrmRangeNm(v);
                }}
              >
                <b>{v} nm</b>
              </button>
            ))}
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" aria-label="VRM minus a tenth" onClick={() => nudgeVrm(-0.1)}>−0.1</button>
            <button type="button" className="acp-btn" aria-label="VRM minus a hundredth" onClick={() => nudgeVrm(-0.01)}>−0.01</button>
            <button type="button" className="acp-btn" aria-label="VRM plus a hundredth" onClick={() => nudgeVrm(0.01)}>+0.01</button>
            <button type="button" className="acp-btn" aria-label="VRM plus a tenth" onClick={() => nudgeVrm(0.1)}>+0.1</button>
          </div>
        </>
      )}

      {/* PI — the parallel index line */}
      <ToggleRow
        title="PI — parallel index"
        detail={
          piOn
            ? `${formatBearing(piBearingDeg)} · ${Math.abs(piOffsetNm).toFixed(2)} nm ${piOffsetNm >= 0 ? 'to starboard' : 'to port'}`
            : 'Off'
        }
        on={piOn}
        onClick={() => setPiOn(!piOn)}
      />
      {piOn && (
        <>
          <div className="acp-layer">
            <div className="lt">
              <b>Index bearing</b>
              <span>{formatBearing(piBearingDeg)}</span>
            </div>
            <input
              className="acp-range"
              style={{ flex: 1, minWidth: 0, maxWidth: 170 }}
              type="range"
              min={0}
              max={359}
              step={1}
              value={Math.round(piBearingDeg)}
              aria-label="Parallel index bearing"
              onChange={(e) => setPiBearingDeg(Number(e.target.value))}
            />
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" aria-label="PI minus 10 degrees" onClick={() => nudgePi(-10)}>−10°</button>
            <button type="button" className="acp-btn" aria-label="PI minus 1 degree" onClick={() => nudgePi(-1)}>−1°</button>
            <button type="button" className="acp-btn" aria-label="PI plus 1 degree" onClick={() => nudgePi(1)}>+1°</button>
            <button type="button" className="acp-btn" aria-label="PI plus 10 degrees" onClick={() => nudgePi(10)}>+10°</button>
          </div>
          <span className="acp-fieldlabel" style={{ marginTop: 8 }}>Offset (passing distance)</span>
          <div className="acp-seg">
            <button
              type="button"
              className={piOffsetNm < 0 ? 'on' : ''}
              onClick={() => { haptic('tick'); setPiOffsetNm(-Math.abs(piOffsetNm)); }}
            >
              Port
            </button>
            <button
              type="button"
              className={piOffsetNm >= 0 ? 'on' : ''}
              onClick={() => { haptic('tick'); setPiOffsetNm(Math.abs(piOffsetNm)); }}
            >
              Starboard
            </button>
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" aria-label="Offset minus a tenth" onClick={() => setPiOffsetNm(piOffsetNm + (piOffsetNm >= 0 ? -0.1 : 0.1))}>−0.1</button>
            <button type="button" className="acp-btn" aria-label="Offset minus a hundredth" onClick={() => setPiOffsetNm(piOffsetNm + (piOffsetNm >= 0 ? -0.01 : 0.01))}>−0.01</button>
            <button type="button" className="acp-btn" aria-label="Offset plus a hundredth" onClick={() => setPiOffsetNm(piOffsetNm + (piOffsetNm >= 0 ? 0.01 : -0.01))}>+0.01</button>
            <button type="button" className="acp-btn" aria-label="Offset plus a tenth" onClick={() => setPiOffsetNm(piOffsetNm + (piOffsetNm >= 0 ? 0.1 : -0.1))}>+0.1</button>
          </div>
          <p className="acp-menu-note">
            Set the bearing to the course past the danger, and the offset to the distance we mean to pass
            it. Steer so the headland or mark rides along the line: on the line is on the passing
            distance, inside it is closer than we planned. The line can also be dragged on the chart.
          </p>
        </>
      )}

      <p className="acp-note">
        Read the bearing off where the EBL lies over what we’re looking at, and the range off where the
        VRM circle touches it. Set them independently — just the bearing, just the range, or both.
      </p>
    </Sheet>
  );
}
